import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * The status colour map — one component, one map, used everywhere (docs/04 §4).
 *
 * Every tint is `/15`, and the uniformity is load-bearing rather than tidiness: the tinted
 * variants were written at `/12`, which is **not on Tailwind's opacity scale**, so those
 * three declarations were dropped at build time and `posted`, `paid`, `overdue`, `submitted`
 * and `approved` all rendered as bare coloured text on a transparent pill while `warning`
 * — the one variant already at `/15` — looked correct. An opacity modifier here must be a
 * multiple of 5 or Tailwind emits nothing at all, silently.
 */
export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-md border px-2.5 py-0.5 text-xs font-medium transition-colors whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'text-foreground',
        neutral: 'border-border bg-muted text-muted-foreground',
        success: 'border-transparent bg-success/15 text-success',
        'success-solid': 'border-transparent bg-success text-success-foreground',
        warning: 'border-transparent bg-warning/15 text-warning',
        destructive: 'border-transparent bg-destructive/15 text-destructive',
        'outline-destructive': 'border-destructive/40 text-destructive',
        info: 'border-transparent bg-info/15 text-info',
        violet: 'border-transparent bg-violet/15 text-violet',
        muted: 'border-transparent bg-muted text-muted-foreground line-through',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export const Badge = ({ className, variant, ...props }) => (
  <span className={cn(badgeVariants({ variant }), className)} {...props} />
);
